import { extendTheme, ThemeConfig } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

const colors = {
  pitch: {
    50: '#E8F1F8',
    100: '#C5DAEE',
    200: '#94B8DB',
    300: '#5A94C4',
    400: '#2A6FA8',
    500: '#074a80',
    600: '#063d6b',
    700: '#053056',
    800: '#042341',
    900: '#03162C',
  },
  gold: {
    400: '#F5B731',
    500: '#E5A020',
    600: '#C8860E',
  },
  slate: {
    850: '#1A2332',
    900: '#121820',
  },
  header: {
    500: '#074a80',
    600: '#063d6b',
  },
};

const theme = extendTheme({
  config,
  colors,
  semanticTokens: {
    colors: {
      'text.default': { default: 'gray.800', _dark: 'gray.100' },
      'text.muted': { default: 'gray.600', _dark: 'gray.400' },
      'text.subtle': { default: 'gray.500', _dark: 'gray.400' },
      'text.accent': { default: 'pitch.600', _dark: 'pitch.300' },
      'surface.card': { default: 'white', _dark: 'slate.850' },
      'surface.input': { default: 'gray.50', _dark: 'gray.800' },
      'border.default': { default: 'gray.200', _dark: 'gray.600' },
    },
  },
  fonts: {
    heading: `'Segoe UI', system-ui, -apple-system, sans-serif`,
    body: `'Segoe UI', system-ui, -apple-system, sans-serif`,
  },
  styles: {
    global: (props: { colorMode: string }) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'slate.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
      },
    }),
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: 'md',
      },
      variants: {
        solid: (props: { colorScheme: string }) => {
          if (props.colorScheme === 'pitch') {
            return {
              bg: 'pitch.600',
              color: 'white',
              _hover: { bg: 'pitch.700' },
            };
          }
          if (props.colorScheme === 'gold') {
            return {
              bg: 'gold.500',
              color: 'slate.900',
              _hover: { bg: 'gold.600' },
            };
          }
          return {};
        },
        outline: (props: { colorScheme: string; colorMode: string }) => {
          if (props.colorScheme === 'pitch') {
            return {
              color: props.colorMode === 'dark' ? 'pitch.200' : 'pitch.600',
              borderColor: props.colorMode === 'dark' ? 'pitch.400' : 'pitch.500',
              _hover: {
                bg: props.colorMode === 'dark' ? 'pitch.900' : 'pitch.50',
              },
            };
          }
          return {};
        },
        ghost: (props: { colorScheme: string; colorMode: string }) => {
          if (props.colorScheme === 'pitch') {
            return {
              color: props.colorMode === 'dark' ? 'gray.200' : 'pitch.600',
              _hover: {
                bg: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'pitch.50',
              },
            };
          }
          return {};
        },
      },
    },
    Card: {
      baseStyle: (props: { colorMode: string }) => ({
        container: {
          borderRadius: 'lg',
          borderWidth: '1px',
          bg: props.colorMode === 'dark' ? 'slate.850' : 'white',
          color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
        },
      }),
    },
    Heading: {
      baseStyle: (props: { colorMode: string }) => ({
        color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
      }),
    },
    Table: {
      variants: {
        simple: (props: { colorMode: string }) => ({
          th: {
            color: props.colorMode === 'dark' ? 'gray.300' : 'gray.600',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
          },
          td: {
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
          },
        }),
      },
    },
    Input: {
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
            _placeholder: {
              color: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
            },
          },
        }),
      },
    },
    NumberInput: {
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
          },
        }),
      },
    },
    Select: {
      variants: {
        outline: (props: { colorMode: string }) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
            color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
          },
        }),
      },
    },
    Tabs: {
      variants: {
        sports: (props: { colorMode: string }) => ({
          tablist: {
            borderBottom: '2px solid',
            borderColor: props.colorMode === 'dark' ? 'pitch.400' : 'pitch.600',
            gap: 2,
          },
          tab: {
            fontWeight: '600',
            color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
            _selected: {
              color: props.colorMode === 'dark' ? 'pitch.200' : 'pitch.700',
              borderColor: props.colorMode === 'dark' ? 'pitch.400' : 'pitch.600',
              borderBottomWidth: '3px',
              bg: 'transparent',
            },
            _hover: {
              color: props.colorMode === 'dark' ? 'gray.200' : 'pitch.600',
            },
          },
        }),
      },
    },
    Menu: {
      baseStyle: (props: { colorMode: string }) => ({
        list: {
          bg: props.colorMode === 'dark' ? 'slate.850' : 'white',
          color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
          borderWidth: '1px',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
          boxShadow: 'lg',
        },
        item: {
          bg: 'transparent',
          color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
            color: props.colorMode === 'dark' ? 'white' : 'gray.900',
          },
          _focus: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
          },
        },
      }),
    },
    Badge: {
      variants: {
        outline: (props: { colorScheme: string; colorMode: string }) => {
          if (props.colorScheme === 'pitch') {
            return {
              color: props.colorMode === 'dark' ? 'pitch.200' : 'pitch.600',
              borderColor: props.colorMode === 'dark' ? 'pitch.400' : 'pitch.500',
            };
          }
          return {};
        },
      },
    },
  },
});

export default theme;
